import { XMLBuilder, XMLParser, XMLValidator } from 'fast-xml-parser';

/**
 * Pure helpers for the XML formatter tool.
 *
 * Pipeline:
 *   1. `XMLValidator.validate()` checks well-formedness AND gives
 *      (line, col) — fast-xml-parser's parser itself silently drops
 *      malformed input and is hard to extract errors from, but the
 *      validator is robust and matches Monaco markers exactly.
 *   2. `XMLParser` builds an ordered AST (`preserveOrder: true`)
 *      preserving comment / CDATA nodes and original attribute order.
 *   3. `XMLBuilder` re-serialises with format=true (pretty) or false
 *      (minified, single-line). Empty elements collapse to self-
 *      closing (`<br/>`) which is the spec-compliant minification.
 *
 * Strict-mode XML: tags MUST close. HTML-style `<br>`, `<img>` will be
 * flagged as unclosed by the validator. The HTML compatibility mode
 * is parked at P1 per the design doc §12.
 */

export interface FormatOk {
  ok: true;
  output: string;
}

export interface FormatErr {
  ok: false;
  error: string;
  /** 1-based line. */
  line?: number;
  /** 1-based column. */
  column?: number;
}

export type XmlResult = FormatOk | FormatErr;

export interface XmlFormatOptions {
  /** Indent space count (1..10, default 2). */
  indent?: number;
}

const COMMENT_KEY = '#comment';
const CDATA_KEY = '__cdata';

/** Pretty-print an XML document with the given indent width. */
export function formatXml(input: string, opts: XmlFormatOptions = {}): XmlResult {
  return roundTrip(input, {
    format: true,
    indentBy: ' '.repeat(clampIndent(opts.indent)),
  });
}

/**
 * Compress an XML document to a single line. Leading / inter-tag
 * whitespace is removed, but text nodes (incl. CDATA / comments) are
 * preserved verbatim.
 */
export function minifyXml(input: string): XmlResult {
  return roundTrip(input, { format: false, indentBy: '' });
}

/* ------------------------------------------------------------------ */

function roundTrip(
  input: string,
  builderOpts: { format: boolean; indentBy: string },
): XmlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };
  // BOM strip — fast-xml-parser tolerates it but our error positions
  // are off by one if we don't.
  const cleaned = input.replace(/^\uFEFF/, '');

  const v = XMLValidator.validate(cleaned, {
    allowBooleanAttributes: true,
  });
  if (v !== true) {
    return {
      ok: false,
      error: v.err.msg,
      line: v.err.line ?? undefined,
      column: v.err.col ?? undefined,
    };
  }

  try {
    const parser = new XMLParser({
      preserveOrder: true,
      ignoreAttributes: false,
      parseTagValue: false,
      parseAttributeValue: false,
      // trimValues: true strips the indentation whitespace text nodes
      // that fast-xml-parser otherwise inserts between siblings (which
      // would then come back doubled when XMLBuilder re-formats with
      // its own indent). CDATA + comments live under their own keys
      // and aren't affected. Trimming inside `<msg>  你好  </msg>`
      // text nodes is a known minor cost; it's a fair trade against
      // the empty-line-flood we'd otherwise inflict on every doc.
      trimValues: true,
      cdataPropName: CDATA_KEY,
      commentPropName: COMMENT_KEY,
      processEntities: true,
    });
    const tree = parser.parse(cleaned);

    const builder = new XMLBuilder({
      preserveOrder: true,
      ignoreAttributes: false,
      cdataPropName: CDATA_KEY,
      commentPropName: COMMENT_KEY,
      suppressEmptyNode: true,
      format: builderOpts.format,
      indentBy: builderOpts.indentBy,
      processEntities: true,
    });
    let out: string = builder.build(tree);

    // fast-xml-parser doesn't preserve the XML prolog when
    // `preserveOrder` strips processing instructions in some
    // versions; if the input had one and the output doesn't, prepend
    // it back so round-tripping is faithful.
    const prologMatch = cleaned.match(/^\s*<\?xml[^?]+\?>/);
    if (prologMatch && !/^\s*<\?xml/.test(out)) {
      out = prologMatch[0] + (builderOpts.format ? '\n' : '') + out;
    }

    // Trim a single trailing newline the builder sometimes appends in
    // pretty mode — Monaco renders the final blank line which is
    // visually noisy.
    if (builderOpts.format && out.endsWith('\n\n')) {
      out = out.replace(/\n+$/, '\n');
    }
    if (!builderOpts.format) {
      out = out.replace(/\n+/g, '');
    }

    return { ok: true, output: out };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

function clampIndent(n: number | undefined): number {
  if (n === undefined) return 2;
  if (n < 1) return 1;
  if (n > 10) return 10;
  return Math.floor(n);
}
